import { PPAI_DAILY_LIMIT_REACHED } from "../../config/ppAiConfig"

const STANDALONE_MAX_INPUT_LENGTH = 10000
function getFriendlyErrorMessage(err, t) {
    const message = err?.message || ""
    if (message.startsWith("MCP_TIMEOUT")) {
        return t("ppai.timeout")
    }
    if (message === "MCP_NETWORK_ERROR") {
        return t("ppai.network_error")
    }
    if (err?.code === PPAI_DAILY_LIMIT_REACHED || /Tageslimit|429|Too Many Requests|limit.*conversation/i.test(message)) {
        return t("ppai.daily_limit_reached")
    }
    return message || t("ppai.error")
}

function parsePlaceholderForGallery(placeholderText) {
    if (!placeholderText || typeof placeholderText !== "string") return { questions: [], footer: "" }
    const lines = placeholderText.split("\n")
    const questions = []
    let footerStart = -1
    const footerMarkers = /jederzeit|any time|tout moment|annulée|beendet|cancel a conversation/i
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const trimmed = line.trim()
        if (trimmed.startsWith("•")) {
            questions.push(trimmed.replace(/^•\s*/, "").trim())
        } else if (footerStart === -1 && footerMarkers.test(trimmed)) {
            footerStart = i
        }
    }
    const footer =
        footerStart >= 0
            ? lines
                  .slice(footerStart)
                  .join("\n")
                  .trim()
            : ""
    return { questions, footer }
}

function extractCsvBlocks(text) {
    const regex = /```csv\s*([\s\S]*?)```/gi
    const blocks = []
    let match
    while ((match = regex.exec(text)) !== null) {
        const csv = match[1]?.trim()
        if (csv) blocks.push(csv)
    }
    return blocks
}

const OFFER_TOOL_NAMES = ["getPrice", "getPriceByGTINs"]
const OFFER_CONTEXT_TOOL_NAMES = ["getPrice", "getPriceByGTINs", "offersMarkdownToCsv"]
const DB_PRICE_TOOL_NAMES = ["getPriceFromDatabase", "queryDatabase", "getPriceFromDB"]

function looksLikePriceQuery(text) {
    if (!text || typeof text !== "string") return false
    return /preis|price|kostet|angebot|günstig|guenstig|teuer|€|\beur\b/i.test(text)
}

function isNoRowsToolResult(result) {
    if (result == null) return false
    const content = Array.isArray(result?.content) ? result.content : []
    const textBlock = content.find((c) => c?.type === "text")
    const text = typeof textBlock?.text === "string" ? textBlock.text : ""
    const normalized = text.replace(/^"|"$/g, "").trim()
    if (normalized === "No rows." || normalized === "No rows") return true
    // Leeres JSON-Array als Antwort (z.B. "[]") gilt ebenfalls als kein Treffer
    if (normalized === "[]") return true
    try {
        const parsed = JSON.parse(normalized)
        if (Array.isArray(parsed) && parsed.length === 0) return true
    } catch {
        // kein JSON – kein leeres Array
    }
    return false
}

function looksLikeDatabasePriceText(str) {
    if (!str || typeof str !== "string") return false
    const s = str.trim()
    return (
        (s.includes("productnr=") || s.includes("ean=")) &&
        (s.includes("; ") || s.includes(";\n")) &&
        (s.includes("id=") || s.includes("product_name=") || s.includes("price_ek="))
    )
}

function getLastDatabasePriceText(messagesList) {
    if (!Array.isArray(messagesList) || messagesList.length === 0) return null
    for (let i = messagesList.length - 1; i >= 0; i--) {
        const msg = messagesList[i]
        if (msg?.role !== "assistant" || !msg?.toolOutputs?.length) continue
        let combined = ""
        for (const tool of msg.toolOutputs) {
            let raw = typeof tool.result === "string" ? tool.result : JSON.stringify(tool.result)
            try {
                const parsed = typeof tool.result === "string" ? JSON.parse(tool.result) : tool.result
                if (parsed?.content && Array.isArray(parsed.content)) {
                    const textBlock = parsed.content.find((c) => c?.type === "text")
                    if (textBlock?.text) raw = textBlock.text
                }
            } catch {
                // keep raw
            }
            if (typeof raw === "string" && looksLikeDatabasePriceText(raw))
                combined += (combined ? "\n" : "") + raw.trim()
        }
        if (combined && looksLikeDatabasePriceText(combined)) return { text: combined, toolOutputs: msg.toolOutputs }
    }
    return null
}

function hasOfferToolOutputs(toolOutputs) {
    if (!Array.isArray(toolOutputs) || toolOutputs.length === 0) return false
    return toolOutputs.some((t) => OFFER_CONTEXT_TOOL_NAMES.includes(t?.name))
}

function isDemoLimitResult(result) {
    if (result == null) return false
    const content = Array.isArray(result?.content) ? result.content : []
    const textBlock = content.find((c) => c?.type === "text")
    const text = typeof textBlock?.text === "string" ? textBlock.text : ""
    return (
        (text.includes("Demo-Limit") || text.includes("Demo-Limit erreicht")) &&
        (text.includes("403") || text.includes("Forbidden"))
    )
}

function userWantsCsv(text) {
    if (!text || typeof text !== "string") return false
    const lower = text.toLowerCase().trim()
    if (!lower.includes("csv")) return false
    return (
        /alle\s+angebot/i.test(text) ||
        /angebot.*csv|csv.*angebot/i.test(text) ||
        /export.*csv|csv.*export/i.test(text) ||
        /download.*csv|csv.*download/i.test(text) ||
        /erstell.*csv|csv.*erstell|schreib.*csv|csv.*schreib/i.test(text)
    )
}

function getLastOfferMarkdown(messagesList) {
    if (!Array.isArray(messagesList) || messagesList.length === 0) return null
    for (let i = messagesList.length - 1; i >= 0; i--) {
        const msg = messagesList[i]
        if (msg?.role !== "assistant" || !msg?.toolOutputs?.length) continue
        const offerTools = msg.toolOutputs.filter((t) => OFFER_TOOL_NAMES.includes(t?.name))
        if (offerTools.length === 0) continue
        const markdown = buildToolMarkdown(offerTools)
        if (markdown && markdown.trim().length > 50) return { markdown, toolOutputs: msg.toolOutputs }
    }
    return null
}

function getToolResultDisplayText(toolResult) {
    if (toolResult == null) return null
    let parsed =
        typeof toolResult === "string"
            ? (() => {
                  try {
                      return JSON.parse(toolResult)
                  } catch {
                      return null
                  }
              })()
            : toolResult
    if (!parsed?.content || !Array.isArray(parsed.content)) return null
    const textParts = parsed.content
        .filter((c) => c?.type === "text")
        .map((c) => c?.text)
        .filter(Boolean)
    if (textParts.length === 0) return null
    let extracted = textParts.join("\n\n")
    if (typeof extracted === "string") {
        const trimmed = extracted.trim()
        if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
            try {
                extracted = JSON.parse(trimmed)
            } catch {
                extracted = trimmed.slice(1, -1).replace(/\\n/g, "\n").replace(/\\"/g, '"')
            }
        }
    }
    return typeof extracted === "string" ? extracted : null
}

function buildToolMarkdown(toolOutputs) {
    if (!toolOutputs?.length) return ""
    const parts = toolOutputs.map((tool) => {
        const displayText = getToolResultDisplayText(tool.result)
        if (displayText != null) return displayText
        return typeof tool.result === "string" ? tool.result : JSON.stringify(tool.result, null, 2)
    })
    return parts.join("\n\n---\n\n")
}

export {
    STANDALONE_MAX_INPUT_LENGTH,
    getFriendlyErrorMessage,
    parsePlaceholderForGallery,
    extractCsvBlocks,
    looksLikePriceQuery,
    isNoRowsToolResult,
    getLastDatabasePriceText,
    hasOfferToolOutputs,
    isDemoLimitResult,
    userWantsCsv,
    getLastOfferMarkdown,
    buildToolMarkdown,
    DB_PRICE_TOOL_NAMES
}
